import { formatLocalizedDate, formatLocalizedNative, formatLocalizedNumber, formatLocalizedUsd } from './optionsLocaleFormatting';

export const DASHBOARD_CURRENCIES = ['usd', 'btc', 'eth'];
export const DASHBOARD_RANGES = ['30d', '90d', 'ytd', 'all', 'custom'];

export const formatUsd = (value, maximumFractionDigits = 2) => {
  return formatLocalizedUsd(value, maximumFractionDigits);
};

export const formatNative = (value, currency, digits = 4) => {
  return formatLocalizedNative(value, currency, digits);
};

export const formatCompactNumber = (value) => {
  return formatLocalizedNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

export const formatChartLabel = (isoString) => {
  return formatLocalizedDate(isoString, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getRangeLabel = (rangeKey, t = null) => {
  const map = {
    '30d': '30D',
    '90d': '90D',
    ytd: 'YTD',
    all: 'ALL',
    custom: t ? t('common.ranges.custom') : 'Custom',
  };
  return map[rangeKey] || rangeKey;
};

export const getCurrencyLabel = (currencyKey) => {
  const map = {
    all: 'All',
    usd: 'USD',
    usdc: 'USDC',
    usdt: 'USDT',
    btc: 'BTC',
    eth: 'ETH',
  };
  return map[currencyKey] || currencyKey.toUpperCase();
};

export const toDateInputValue = (date) => {
  return date.toISOString().slice(0, 10);
};

export const getDefaultCustomRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - 30);
  return {
    startDate: toDateInputValue(start),
    endDate: toDateInputValue(end),
  };
};

export const buildCustomRangePayload = ({ startDate, endDate }) => {
  if (!startDate || !endDate) return { start: null, end: null };
  return {
    start: `${startDate}T00:00:00+00:00`,
    end: `${endDate}T23:59:59+00:00`,
  };
};

export const groupHistoryIntoUsdSeries = (items) => {
  const grouped = new Map();

  items.forEach((item) => {
    const existing = grouped.get(item.snapshot_at) || {
      snapshot_at: item.snapshot_at,
      snapshot_timestamp_ms: item.snapshot_timestamp_ms,
      total_usd: 0,
      btc_equity_native: null,
      eth_equity_native: null,
    };

    existing.total_usd += Number(item.equity_usd || 0);
    if (item.currency === 'BTC') existing.btc_equity_native = item.equity_native;
    if (item.currency === 'ETH') existing.eth_equity_native = item.equity_native;

    grouped.set(item.snapshot_at, existing);
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.snapshot_timestamp_ms - b.snapshot_timestamp_ms)
    .map((item) => ({
      ...item,
      label: formatChartLabel(item.snapshot_at),
      value: Number(item.total_usd || 0),
    }));
};

export const buildSelectedHistorySeries = (items, selectedCurrency) => {
  if (selectedCurrency === 'usd') {
    return groupHistoryIntoUsdSeries(items);
  }

  const targetCurrency = selectedCurrency.toUpperCase();
  return items
    .filter((item) => item.currency === targetCurrency)
    .sort((a, b) => a.snapshot_timestamp_ms - b.snapshot_timestamp_ms)
    .map((item) => ({
      snapshot_at: item.snapshot_at,
      snapshot_timestamp_ms: item.snapshot_timestamp_ms,
      label: formatChartLabel(item.snapshot_at),
      value: Number(item.equity_native || 0),
      valueUsd: item.equity_usd,
    }));
};

export const getSelectedAccountCurrency = (accountData, selectedCurrency) => {
  if (!accountData?.account_equity?.currencies?.length) return null;
  if (selectedCurrency === 'usd') return null;
  return accountData.account_equity.currencies.find((item) => item.currency === selectedCurrency.toUpperCase()) || null;
};

export const getSelectedPerformanceCurrency = (performanceData, selectedCurrency) => {
  if (selectedCurrency === 'usd' || !performanceData) return null;
  const collection = performanceData.currencies || performanceData.by_currency || [];
  return collection.find((item) => item.currency === selectedCurrency.toUpperCase()) || null;
};

export const getOpenOperationsCount = ({ accountData, selectedCurrency }) => {
  if (!accountData?.trading_pnl) return 0;
  if (selectedCurrency === 'usd') {
    return accountData.trading_pnl.totals_usd?.open_operations_count || 0;
  }
  const current = getSelectedPerformanceCurrency(accountData.trading_pnl, selectedCurrency);
  return current?.open_operations_count || 0;
};

export const getTradingCardNumbers = ({ performanceData, selectedCurrency }) => {
  if (!performanceData) {
    return {
      headline: '—',
      headlineSecondary: null,
      realized: '—',
      unrealized: '—',
      fees: null,
      returnPct: null,
    };
  }

  if (selectedCurrency === 'usd') {
    return {
      headline: formatUsd(performanceData.totals_usd?.net_pnl_usd),
      headlineSecondary: null,
      realized: formatUsd(performanceData.totals_usd?.realized_pnl_usd),
      unrealized: formatUsd(performanceData.totals_usd?.unrealized_pnl_usd),
      fees: performanceData.totals_usd?.fees_usd === null || performanceData.totals_usd?.fees_usd === undefined ? '—' : `-${formatUsd(Math.abs(Number(performanceData.totals_usd.fees_usd)))}`,
      returnPct: performanceData.return_pct,
    };
  }

  const current = getSelectedPerformanceCurrency(performanceData, selectedCurrency);
  if (!current) {
    return {
      headline: '—',
      headlineSecondary: null,
      realized: '—',
      unrealized: '—',
      fees: null,
      returnPct: null,
    };
  }

  const currencyCode = selectedCurrency.toUpperCase();
  return {
    headline: formatNative(current.net_pnl_native, currencyCode, 4),
    headlineSecondary: current.net_pnl_usd !== null && current.net_pnl_usd !== undefined ? `≈ ${formatUsd(current.net_pnl_usd)}` : null,
    realized: `${formatNative(current.realized_pnl_native, currencyCode, 4)}${current.realized_pnl_usd !== null && current.realized_pnl_usd !== undefined ? ` · ≈ ${formatUsd(current.realized_pnl_usd)}` : ''}`,
    unrealized: `${formatNative(current.unrealized_pnl_native, currencyCode, 4)}${current.unrealized_pnl_usd !== null && current.unrealized_pnl_usd !== undefined ? ` · ≈ ${formatUsd(current.unrealized_pnl_usd)}` : ''}`,
    fees: `${current.fees_native === null || current.fees_native === undefined ? '—' : `-${formatNative(Math.abs(Number(current.fees_native)), currencyCode, 4)}`}${current.fees_usd !== null && current.fees_usd !== undefined ? ` · ≈ -${formatUsd(Math.abs(Number(current.fees_usd)))}` : ''}`,
    returnPct: performanceData.return_pct,
  };
};

export const getAccountValuePresentation = ({ accountData, selectedCurrency, t = null }) => {
  const totalLabel = t ? t('dashboard.accountValueHeadline') : 'Total Account Value';
  const fallbackLabel = t ? t('dashboard.accountValueFallback') : 'Account Value';
  const accountLabel = (currency) => t ? t('dashboard.accountLabel').replace('{currency}', currency) : `${currency} Account`;
  const accountValueLabel = (currency) => t ? t('dashboard.accountValueLabel').replace('{currency}', currency) : `${currency} Account Value`;
  const availableFundsLabel = t ? t('dashboard.availableFunds') : 'Available Funds';
  const marginBalanceLabel = t ? t('dashboard.marginBalance') : 'Margin Balance';

  if (!accountData?.account_equity) {
    return {
      headlineLabel: totalLabel,
      headlineValue: '—',
      headlineSecondary: null,
      rows: [],
    };
  }

  const currencies = accountData.account_equity.currencies || [];
  if (selectedCurrency === 'usd') {
    return {
      headlineLabel: totalLabel,
      headlineValue: formatUsd(accountData.account_equity.total_account_value_usd),
      headlineSecondary: null,
      rows: currencies.map((item) => ({
        key: item.currency,
        label: accountLabel(item.currency),
        value: formatNative(item.equity_native, item.currency, item.currency === 'BTC' ? 4 : 2),
        secondary: `≈ ${formatUsd(item.equity_usd)}`,
      })),
    };
  }

  const current = getSelectedAccountCurrency(accountData, selectedCurrency);
  if (!current) {
    return {
      headlineLabel: fallbackLabel,
      headlineValue: '—',
      headlineSecondary: null,
      rows: [],
    };
  }

  return {
    headlineLabel: accountValueLabel(current.currency),
    headlineValue: formatNative(current.equity_native, current.currency, current.currency === 'BTC' ? 4 : 2),
    headlineSecondary: `≈ ${formatUsd(current.equity_usd)}`,
    rows: [
      {
        key: `${current.currency}-available`,
        label: availableFundsLabel,
        value: formatNative(current.available_funds_native, current.currency, current.currency === 'BTC' ? 4 : 2),
        secondary: current.available_funds_usd !== null && current.available_funds_usd !== undefined ? `≈ ${formatUsd(current.available_funds_usd)}` : null,
      },
      {
        key: `${current.currency}-margin`,
        label: marginBalanceLabel,
        value: formatNative(current.margin_balance_native, current.currency, current.currency === 'BTC' ? 4 : 2),
        secondary: current.margin_balance_usd !== null && current.margin_balance_usd !== undefined ? `≈ ${formatUsd(current.margin_balance_usd)}` : null,
      },
    ],
  };
};

export const getChartTitle = (selectedCurrency, t = null) => {
  if (selectedCurrency === 'usd') return t ? t('dashboard.accountEquityTitle') : 'Account Equity';
  if (t) return t('dashboard.accountEquityTitleCurrency').replace('{currency}', selectedCurrency.toUpperCase());
  return `${selectedCurrency.toUpperCase()} Account Equity`;
};

export const getChartValueFormatter = (selectedCurrency) => {
  if (selectedCurrency === 'usd') {
    return (value) => formatUsd(value, 0);
  }
  return (value) => formatNative(value, selectedCurrency.toUpperCase(), selectedCurrency === 'btc' ? 4 : 2);
};
